"use client";

import { useCallback, useRef, useState } from "react";

/**
 * Small hook for wiring "click → async action → show spinner" without sprinkling
 * useState everywhere. Returns `{ run, busy, error }`.
 *
 *   const archive = useAsyncAction(async (id: string) => fetch(...));
 *   <Button onClick={() => archive.run(id)} disabled={archive.busy}>
 *     {archive.busy && <Loader2 />} Archive
 *   </Button>
 */
export function useAsyncAction<Args extends unknown[], R>(
  fn: (...args: Args) => Promise<R>,
) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const mounted = useRef(true);

  const run = useCallback(
    async (...args: Args) => {
      setBusy(true);
      setError(null);
      try {
        return await fn(...args);
      } catch (e) {
        if (mounted.current) setError(e);
        throw e;
      } finally {
        if (mounted.current) setBusy(false);
      }
    },
    [fn],
  );

  return { run, busy, error };
}
